import { useState, useEffect } from 'react';
/**
 * there are places in our codebase where we change the window.location itself
 * instead of using the react-router. Sometimes, it's because we have to
 * route to an angular page programmatically, and changing the window object's location
 * is the easiest way of doing so. Other times, I suspect, it's due to copy/pasta-ing existing code.
 *
 * Regardless, in order to make sure that the toast container appears where it is supposed to
 * when the route changes, we can't simply rely on react-router useLocation changes.
 * react-use's useLocation comes to the rescue.
 */
import { useLocation } from 'react-use';
import { ToastLocations } from '../constants';

/**
 * checks where the toasts container should be located, based off of the url of the page.
 * we display the container in different places to make sure important actions aren't unobstructed.
 * see https://iterable.atlassian.net/browse/FE-361 for details.
 */
export const useToastsContainerLocation = () => {
  const [toastsLocation, setToastsLocation] = useState<
    (typeof ToastLocations)[keyof typeof ToastLocations]
  >(ToastLocations.Standard);
  const location = useLocation();

  useEffect(() => {
    // campaign editor with a the template editor out follows the following pattern:
    //
    const isCampaignEditorWithTemplateEditor =
      location.pathname?.includes('campaigns') && location.search?.includes('templateEditor');

    // journey url with a drawer follows the following pattern:
    // https://app.iterable.com/workflows/:workflowId/edit?mode=beta&workflowType=Draft
    const isWorkflowStudioWithDrawerOut =
      location.pathname?.includes('workflows') && location.search?.includes('workflowType=Draft');

    // journey url with a drawer follows the following pattern:
    // https://app.iterable.com/workflows/:workflowId/edit?mode=beta&workflowType=Published
    const isWorkflowStudidLayout =
      location.pathname?.includes('workflows') &&
      location.search?.includes('workflowType') &&
      !location.search?.includes('workflowType=Draft');

    // template studio url follows the following pattern:
    // https://app.iterable.com/templates/editor?templateId=:templateId&view=content
    const isTemplatesStudio =
      location.pathname?.includes('templates/editor') && location.search?.includes('view=');

    if (isWorkflowStudioWithDrawerOut) {
      setToastsLocation(ToastLocations.StudioDrawer);
    } else if (isCampaignEditorWithTemplateEditor) {
      setToastsLocation(ToastLocations.CampaignEditorForTemplateEditor);
    } else if (isWorkflowStudidLayout || isTemplatesStudio) {
      setToastsLocation(ToastLocations.Studio);
    } else {
      setToastsLocation(ToastLocations.Standard);
    }
  }, [location.pathname, location.search]);

  return toastsLocation;
};
